import { Config } from "../../../../../Config";
import { Const } from "../../../../Const";
import { LayaGL } from "../../../../layagl/LayaGL";
import { Vector4 } from "../../../../maths/Vector4";
import { BaseTexture } from "../../../../resource/BaseTexture";
import { Texture2DArray } from "../../../../resource/Texture2DArray";
import { Texture } from "../../../../resource/Texture";
import { Texture2D } from "../../../../resource/Texture2D";
import { BlendModeHandler } from "../../../canvas/BlendMode";
import { ShaderDefines2D } from "../ShaderDefines2D";
const _TEMP_CLIPDIR = new Vector4(Const.MAX_CLIP_SIZE, 0, 0, Const.MAX_CLIP_SIZE);
export class GraphicsShaderInfo {
    constructor() {
        this._enableVertexSize = false;
        this._materialClip = false;
        this._fillTexture = false;
        this._clipMatDir = null;
        this._clipMatPos = null;
        this._texRange = null;
        this._vertexSize = null;
        this._isTextrueReadGamma = false;
        this.texArrayLayer = 0;
        this._blend = null;
        this.shaderData = LayaGL.renderDeviceFactory.createShaderData();
        this.shaderData.addDefine(ShaderDefines2D.TEXTURESHADER);
        this.shaderData.addDefine(ShaderDefines2D.VERTEXALPHA);
        if (Config.uvClipMode === "gpu") {
            this.shaderData.addDefine(ShaderDefines2D.UV_CLIP_GPU);
        }
        this.toDefault();
        BlendModeHandler.initBlendMode(this.shaderData);
    }
    toDefault() {
        if (this._clipMatDir !== _TEMP_CLIPDIR) {
            this.clipMatDir = _TEMP_CLIPDIR;
        }
        if (this._clipMatPos !== Vector4.ZERO) {
            this.clipMatPos = Vector4.ZERO;
        }
        this._textureHost = null;
        this.enableVertexSize = false;
        this.materialClip = false;
        this.fillTexture = false;
    }
    get textureHost() {
        return this._textureHost;
    }
    set textureHost(value) {
        this._textureHost = value;
        let textrueReadGamma = false;
        if (value) {
            if (value instanceof BaseTexture) {
                textrueReadGamma = this.textureHost.gammaCorrection != 1;
            }
            else if (value instanceof Texture && value.bitmap) {
                textrueReadGamma = value.bitmap.gammaCorrection != 1;
            }
        }
        if (textrueReadGamma != this._isTextrueReadGamma) {
            this._isTextrueReadGamma = textrueReadGamma;
            if (textrueReadGamma) {
                this.shaderData.addDefine(ShaderDefines2D.GAMMATEXTURE);
            }
            else {
                this.shaderData.removeDefine(ShaderDefines2D.GAMMATEXTURE);
            }
        }
        let tex;
        if (value instanceof Texture) {
            tex = value.bitmap;
        }
        else {
            tex = value;
        }
        if (!tex) {
            tex = Texture2D.whiteTexture;
        }
        if (this._bitmap != tex) {
            this._bitmap = tex;
            if (tex instanceof Texture2DArray) {
                this.shaderData.addDefine(ShaderDefines2D.USE_TEX_ARRAY);
                this.shaderData.setTexture(ShaderDefines2D.UNIFORM_SPRITETEXTURE_ARRAY, tex);
            }
            else {
                this.shaderData.removeDefine(ShaderDefines2D.USE_TEX_ARRAY);
                this.shaderData.setTexture(ShaderDefines2D.UNIFORM_SPRITETEXTURE, tex);
            }
        }
    }
    set enableVertexSize(value) {
        if (value == this._enableVertexSize)
            return;
        this._enableVertexSize = value;
        if (value) {
            this.shaderData.addDefine(ShaderDefines2D.VERTEX_SIZE);
            this.shaderData.removeDefine(ShaderDefines2D.VERTEXALPHA);
        }
        else {
            this.shaderData.addDefine(ShaderDefines2D.VERTEXALPHA);
            this.shaderData.removeDefine(ShaderDefines2D.VERTEX_SIZE);
        }
    }
    get enableVertexSize() {
        return this._enableVertexSize;
    }
    set vertexSize(value) {
        this._vertexSize = value;
        this.shaderData.setVector(ShaderDefines2D.UNIFORM_VERTEX_SIZE, value);
    }
    get vertexSize() {
        return this._vertexSize;
    }
    set materialClip(value) {
        if (value == this._materialClip)
            return;
        this._materialClip = value;
        if (value) {
            this.shaderData.addDefine(ShaderDefines2D.MATERIALCLIP);
        }
        else {
            this.shaderData.removeDefine(ShaderDefines2D.MATERIALCLIP);
        }
    }
    get materialClip() {
        return this._materialClip;
    }
    set clipMatDir(value) {
        this._clipMatDir = value;
        this.shaderData.setVector(ShaderDefines2D.UNIFORM_MATERIAL_CLIPMATDIR, value);
    }
    get clipMatDir() {
        return this._clipMatDir;
    }
    set clipMatPos(value) {
        this._clipMatPos = value;
        this.shaderData.setVector(ShaderDefines2D.UNIFORM_MATERIAL_CLIPMATPOS, value);
    }
    get clipMatPos() {
        return this._clipMatPos;
    }
    get u_TexRange() {
        return this._texRange;
    }
    set u_TexRange(value) {
        this._texRange = value;
        this.shaderData.setVector(ShaderDefines2D.UNIFORM_TEXRANGE, value);
    }
    set fillTexture(value) {
        if (value == this._fillTexture)
            return;
        this._fillTexture = value;
        if (value) {
            this.shaderData.addDefine(ShaderDefines2D.FILLTEXTURE);
        }
        else {
            this.shaderData.removeDefine(ShaderDefines2D.FILLTEXTURE);
        }
    }
    get fillTexture() {
        return this._fillTexture;
    }
    cloneTo(shaderData) {
        if (this.enableVertexSize) {
            shaderData.addDefine(ShaderDefines2D.VERTEX_SIZE);
            shaderData.setVector(ShaderDefines2D.UNIFORM_VERTEX_SIZE, this.vertexSize);
        }
        if (this.materialClip) {
            shaderData.addDefine(ShaderDefines2D.MATERIALCLIP);
            shaderData.setVector(ShaderDefines2D.UNIFORM_MATERIAL_CLIPMATDIR, this.clipMatDir);
            shaderData.setVector(ShaderDefines2D.UNIFORM_MATERIAL_CLIPMATPOS, this.clipMatPos);
        }
        let textrueReadGamma = this.shaderData.hasDefine(ShaderDefines2D.GAMMATEXTURE);
        if (textrueReadGamma) {
            shaderData.addDefine(ShaderDefines2D.GAMMATEXTURE);
        }
        else {
            shaderData.removeDefine(ShaderDefines2D.GAMMATEXTURE);
        }
        let tex = this._textureHost.bitmap;
        if (!tex) {
            tex = this._textureHost;
        }
        let fill = this.shaderData.hasDefine(ShaderDefines2D.FILLTEXTURE);
        if (fill) {
            shaderData.addDefine(ShaderDefines2D.FILLTEXTURE);
            shaderData.setVector(ShaderDefines2D.UNIFORM_TEXRANGE, this.u_TexRange);
        }
        else {
            shaderData.removeDefine(ShaderDefines2D.FILLTEXTURE);
        }
        if (tex instanceof Texture2DArray) {
            shaderData.addDefine(ShaderDefines2D.USE_TEX_ARRAY);
            shaderData.setTexture(ShaderDefines2D.UNIFORM_SPRITETEXTURE_ARRAY, tex);
        }
        else {
            shaderData.removeDefine(ShaderDefines2D.USE_TEX_ARRAY);
            shaderData.setTexture(ShaderDefines2D.UNIFORM_SPRITETEXTURE, tex);
        }
    }
    clear() {
        if (this._blend) {
            BlendModeHandler.initBlendMode(this.shaderData);
            this._blend = null;
        }
        this.toDefault();
    }
    destroy() {
        this.shaderData.destroy();
        this._textureHost = null;
    }
}
